/**
 * Store Pinia des productions (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed, DeepReadonly } from "vue";
import { IProduction, StateOfSampling, StateOfProduction } from "../models/production";
import { IProductionApiResponse } from "../models/api/productionApiResponse";
import { IProductionFilter } from "../models/filters/productionFilter";
import { IProductionForm } from "../models/forms/productionForm";
import ProductionService from "../services/ProductionService";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";
import { ICenter } from "../models/center";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IProductionSamplingEvent } from "../models/productionSamplingEvent";
import { IProductionDilutorBath } from "../models/productionDilutorBath";
import { useProductionSamplingEventStore } from "./productionSamplingEvent.store";
import { useProductionDilutorBathStore } from "./productionDilutorBath.store";
import { useProductionStaticStore } from "./productionStatic.store";
import { useProductionSampleStore } from "./productionSample.store";
import { useAgentStore } from "./agent.store";
import { useParameterStore } from "./parameter.store";
import { useCenterStore } from "./center.store";
import { useBoars } from "./boars.store";
import { IBoar } from "../models/boar";
import AnimalService from "../services/AnimalService";
import AgentService from "../services/AgentService";
import { IAgentApiResponse } from "../models/api/agentApiResponse";
import { IAgent } from "../models/agent";
import { getDateNumericFormat } from "../helper/dateFormatter";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";
import { inFlight } from "../helper/inFlight";

/**
 * Store Pinia des productions utilisant la Composition API
 */
export const useProductionStore = defineStore("productions", () => {
    // ─── Cache de résolution cross-centre ────────────────────────────────────
    // Les verrats mutés vers un autre centre et les agents d'un autre centre (lot externe reçu) ne
    // sont JAMAIS dans boarsStore / agentStore, qui ne portent que le centre courant. Les fallbacks
    // de hydrateProductions les récupéraient un par un dans des Maps LOCALES à l'appel, détruites
    // au retour : l'hydratation suivante les considérait de nouveau manquants et refaisait les
    // mêmes appels. Mesure du 2026-08-28, sur un SEUL premier affichage du Suivi fabrication
    // (centre 11) : 17 GET /boars/56667, 11 GET /agents/11, 11 GET /agents/42, plus 97 GET
    // /boars/search?IsPresent=false déclenchés par le fallback « réformés » — lequel ne peut de
    // toute façon pas résoudre ces verrats, puisqu'il filtre sur le centre courant.
    //
    // Ces entités ne sont volontairement PAS poussées dans les stores : getAnimauxFilter
    // (boars.store) fait du filtrage client-side sur `datas`, et un verrat d'un autre centre y
    // apparaîtrait dans les listes du centre courant. Cache vidé au changement de centre (load()).
    // Volontairement dans le setup et non au niveau module : sa durée de vie est celle du store.
    const crossCenterBoars = new Map<number, DeepReadonly<IBoar>>();
    const crossCenterAgents = new Map<number, IAgent>();
    // Ids demandés dont l'API n'a rien rendu : sans cette mémoire, un id supprimé ou hors périmètre
    // est redemandé à chaque hydratation, indéfiniment.
    const unresolvableBoarIds = new Set<number>();
    const unresolvableAgentIds = new Set<number>();
    // Même mémoire, un cran plus haut : productions demandées par id que l'API ne rend pas. Sans
    // elle, hydrateManufactoringProductions les recalcule « manquantes » à CHAQUE hydratation et
    // les redemande — or le suivi de fabrication réhydrate à chaque changement d'onglet, chaque
    // enregistrement et chaque message SignalR.
    const unresolvableProductionIds = new Set<number>();

    /** Purge les résolutions cross-centre : elles sont relatives au centre chargé. */
    function clearCrossCenterCaches() {
        crossCenterBoars.clear();
        crossCenterAgents.clear();
        unresolvableBoarIds.clear();
        unresolvableAgentIds.clear();
        unresolvableProductionIds.clear();
    }

    // State
    const datas = ref<IProduction[]>([]);
    const datasHistorique = ref<IProduction[]>([]);
    const current = ref<IProduction>({} as IProduction);
    const itemForm = ref<IProductionForm>({} as IProductionForm);
    const isLoading = ref(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    const loadedDateFrom = ref<Date | undefined>(undefined);

    // Getters

    /**
     * Ne retourne que les productions créés entre les deux dates données
     */
    const getProductionCreatedDuringTimespan = computed(() => (date1: Date, date2: Date): IProduction[] => {
        const previousDay = new Date(date1);
        previousDay.setHours(0, 0, 0, 0);

        const nextDay = new Date(date2);
        nextDay.setHours(0, 0, 0, 0);

        const isSameDay = previousDay.getDate() === nextDay.getDate();
        isSameDay ? nextDay.setDate(nextDay.getDate() + 1) : null;

        if (datas.value.length > 0) {
            const seen = new Map<string, IProduction>();
            for (const production of datas.value) {
                const d = new Date(production.dateProduction);
                if (d >= previousDay && d <= nextDay) {
                    const key = `${production.dateProduction}|${production.animal.id}`;
                    if (!seen.has(key)) seen.set(key, production);
                }
            }
            return Array.from(seen.values());
        }

        return [];
    });

    /**
     * Retourne si au moins une production entre les deux dates
     */
    const wereProductionCreatedDuringTimespan = computed(() => (date1: Date, date2: Date): boolean => {
        return false;
        return datas.value.some((b) => new Date(b.addingDate!).setHours(0, 0, 0, 0) >= new Date(date1).setHours(0, 0, 0, 0) &&
            new Date(b.addingDate!).setHours(0, 0, 0, 0) <= new Date(date2).setHours(0, 0, 0, 0));
    });

    /**
     * Retourne la date de production la plus récente avant une date donnée
     */
    const getPreviousProductionDate = computed(() => (date: Date): Date | null => {
        const prevDate = new Date(date);
        prevDate.setHours(0, 0, 0, 0);

        const productions = datas.value
            .map((production) => new Date(production.dateProduction))
            .filter((productionDate) => productionDate < prevDate)
            .sort((a, b) => b.getTime() - a.getTime());

        return productions.length > 0 ? productions[0] : null;
    });

    /**
     * Retourne la date de production la plus récente après une date donnée
     */
    const getNextProductionDate = computed(() => (date: Date): Date | null => {
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);

        const productions = datas.value
            .map((production) => new Date(production.dateProduction))
            .filter((productionDate) => productionDate >= nextDay)
            .sort((a, b) => a.getTime() - b.getTime());

        return productions.length > 0 ? productions[0] : null;
    });

    /**
     * Retourne le numéro de production en fonction de la date et de l'animal
     */
    const getAnimalProductionNumber = computed(() => (animalId: number, dateProd: Date, searchHistorique: boolean = false) => {
        let dataAnimaux = datas.value;

        if (searchHistorique) {
            dataAnimaux = datasHistorique.value;
        }

        let orderedProductionsWithTime = dataAnimaux.filter(p => p.animal?.id == animalId && getDateNumericFormat(p.dateProduction) == getDateNumericFormat(dateProd))
            .sort(function (a, b) {
                return new Date(a.dateProduction).getTime() - new Date(b.dateProduction).getTime();
            })
            .map(p => ({
                animalId: p.animal?.id,
                dateProd: p.dateProduction
            }))

        var numberProductionSameDay = orderedProductionsWithTime.length

        if (numberProductionSameDay > 1) {
            numberProductionSameDay = orderedProductionsWithTime.findIndex(p => p.dateProd == dateProd) + 1
        } else {
            numberProductionSameDay = 1;
        }

        return numberProductionSameDay
    });

    /**
     * Retourne le numéro de production en fonction de la date et de l'animal
     */
    const getNumberProductionOfDay = computed(() => (animalId: number, dateProd: Date) => {
        let orderedProductions = datas.value.filter(p => p.animal?.id == animalId)
            .sort(function (a, b) {
                return new Date(a.dateProduction).getTime() - new Date(b.dateProduction).getTime();
            })
            .map(p => ({
                animalId: p.animal?.id,
                dateProd: getDateNumericFormat(p.dateProduction)
            }))

        return orderedProductions.filter(p => p.dateProd == getDateNumericFormat(dateProd)).length
    });

    /**
     * Retourne le numéro de production en fonction de la date de production
     */
    const getProductionNumberForDay = computed(() => (dateProd: Date) => {
        // IMPORTANT : trier une COPIE. `datas.value.sort(...)` muterait le tableau réactif EN PLACE
        // dans un computed → auto-invalidation → tempête de ré-évaluations (fige la page pendant la
        // pré-dilution quand saveProduction réordonne `datas`). Cf. bug freeze % Vivants / pré-dilution.
        let orderedProductions = [...datas.value]
            .sort(function (a, b) {
                return new Date(a.dateProduction).getTime() - new Date(b.dateProduction).getTime();
            })
            .map(p => ({
                animalId: p.animal?.id,
                dateProd: getDateNumericFormat(p.dateProduction),
                dateTimeProduction: new Date(p.dateProduction).getTime(),
                numberDayProduction: p.numberDayProduction
            }))

        var listProduction = orderedProductions.filter(p => p.dateProd == getDateNumericFormat(dateProd)).sort(function (a, b) {
            return new Date(a.dateTimeProduction).getTime() - new Date(b.dateTimeProduction).getTime();
        })

        if (listProduction.length > 0) {
            return listProduction[listProduction.length - 1].numberDayProduction + 1
        } else {
            return 1
        }
    });

    const getStatusCodeProduction = computed(() => (production: IProduction) => {

        // Mantis #26185 - Intitule historique de production
        var codeProduction = ""

        if (production.blankSample) {
            codeProduction = "P"
        } else {

            var isObservationsNotDone = production.productionState.toString() == StateOfProduction[StateOfProduction.IsWaiting].toString();

            // Si l'état du prélèvement est Pure/Brut ou Prédilution et que les observations ne sont pas faites
            if ((production.samplingState.toString() == StateOfSampling[StateOfSampling.Clean].toString() || production.samplingState.toString() == StateOfSampling[StateOfSampling.PreDiluted].toString() || production.samplingState.toString() == StateOfSampling[StateOfSampling.PreDilutedOneByOne].toString()) && isObservationsNotDone) {
                codeProduction = "A"
            } else {
                // Fabriqué (observations et conformes)
                if (production.productionState.toString() == StateOfProduction[StateOfProduction.Compliant].toString()) {
                    codeProduction = "F"
                } else {
                    // Non-conforme (observations et non-conformes)
                    codeProduction = "N"
                }
            }
        }

        return codeProduction
    });

    const getProductionsDesc = computed((): IProduction[] => {
        var resultData = datas.value.filter(p => p.animal?.id == itemForm.value.animalId).sort((a, b) => (getDateNumericFormat(new Date(a.dateProduction)) - getDateNumericFormat(new Date(b.dateProduction))) || (a.animal?.geneticType?.code3 ?? '').localeCompare(b.animal?.geneticType?.code3 ?? ''))
        return resultData;
    });

    const getProductionsAsc = computed((): IProduction[] => {
        return datas.value.filter(p => p.animal?.id == itemForm.value.animalId).sort((a, b) => (getDateNumericFormat(new Date(b.dateProduction)) - getDateNumericFormat(new Date(a.dateProduction))));
    });

    // Actions

    /**
     * Hydrate une liste de productions en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param productions Liste de productions reçues de l'API avec des IDs
     * @returns Liste de productions avec les références des stores
     */
    async function hydrateProductions(productions: IProductionApiResponse[], centerId?: number, perfPage?: string): Promise<IProduction[]> {
        const { start: perfStart, end: perfEnd } = usePerformanceTracker(perfPage);
        if (productions.length === 0) return [];

        // 1. Récupérer les stores une seule fois
        const parameterStore = useParameterStore();
        const centerStore = useCenterStore();
        const agentStore = useAgentStore();
        const boarsStore = useBoars();
        const productionStaticStore = useProductionStaticStore();
        const productionSampleStore = useProductionSampleStore();
        const productionDilutorBathStore = useProductionDilutorBathStore();

        // 2. Charger les stores dépendants en parallèle si nécessaire
        // Helper : attendre qu'un store finisse son chargement en cours
        const waitForStore = (store: { isLoading: boolean }) =>
            new Promise<void>(resolve => {
                if (!store.isLoading) return resolve();
                const interval = setInterval(() => {
                    if (!store.isLoading) { clearInterval(interval); resolve(); }
                }, 50);
            });

        const loadPromises: Promise<unknown>[] = [];

        // Verrou d'appel en vol : hydrateProductions est appelé en parallèle (une fois par lot
        // externe résolu par hydrateLots), et chaque appel évaluait ces conditions avant que le
        // premier chargement n'ait rempli le store. Les clés portent le centre là où le contenu
        // en dépend.
        if (parameterStore.datas.length === 0) {
            loadPromises.push(inFlight('parameter:all', () => parameterStore.load({ page: 1, pageSize: 0 })));
        }

        if (centerStore.datas.length === 0) {
            loadPromises.push(inFlight('center:all', () => centerStore.load({ page: 1, pageSize: 0 })));
        }

        if (agentStore.datas.length === 0) {
            if (agentStore.isLoading) {
                loadPromises.push(waitForStore(agentStore));
            } else {
                loadPromises.push(inFlight('agent:all', () => agentStore.load({ page: 1, pageSize: 0 })));
            }
        }

        if (boarsStore.datas.length === 0) {
            if (boarsStore.isLoading) {
                loadPromises.push(waitForStore(boarsStore));
            } else {
                loadPromises.push(inFlight(`boars:center:${centerId}`, () => boarsStore.load({ page: 1, pageSize: 0, idCenter: centerId })));
            }
        }

        if (loadPromises.length > 0) {
            perfStart('  ↳ stores feuilles (agents, verrats, params)');
            await Promise.all(loadPromises);
            perfEnd('  ↳ stores feuilles (agents, verrats, params)', 'api');
        }

        // 3. Créer des Maps d'index pour des lookups O(1)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const agentMap = new Map(agentStore.datas.map(a => [a.id, a]));
        let boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));

        // Réinjecter les entités cross-centre déjà résolues lors d'une hydratation précédente
        // AVANT les fallbacks : sans ça, chacun les redécouvre « manquantes » et refait ses appels.
        for (const [id, boar] of crossCenterBoars) if (!boarsMap.has(id)) boarsMap.set(id, boar);
        for (const [id, agent] of crossCenterAgents) if (!agentMap.has(id)) agentMap.set(id, agent);

        // Fallback 1 : verrat réformé (isPresent=false sur le centre courant)
        // Un id déjà constaté introuvable ne doit PAS le redéclencher : loadReformed() filtre sur
        // le centre courant, il ne résoudra jamais un verrat muté ailleurs, et le relancer à chaque
        // hydratation coûtait 97 GET /boars/search sur un seul affichage.
        const needsReformedLookup = productions.some(
            p => !boarsMap.has(p.animalId) && !unresolvableBoarIds.has(p.animalId)
        );
        if (needsReformedLookup) {
            perfStart('  ↳ verrats réformés (loadReformed)');
            await boarsStore.loadReformed();
            perfEnd('  ↳ verrats réformés (loadReformed)', 'api');
            boarsMap = new Map(boarsStore.datas.map(b => [b.id, b]));
            for (const [id, boar] of crossCenterBoars) if (!boarsMap.has(id)) boarsMap.set(id, boar);
        }

        // Fallback 2 : verrat muté vers un autre centre (absent de loadReformed car centerId différent)
        // Pour chaque animalId encore manquant, appel API individuel puis hydratation dans la map
        const stillMissingIds = [...new Set(
            productions.map(p => p.animalId).filter(id => !boarsMap.has(id) && !unresolvableBoarIds.has(id))
        )];
        if (stillMissingIds.length > 0) {
            perfStart('  ↳ verrats cross-centre');
            const fetched = await Promise.all(stillMissingIds.map(id => AnimalService.getBoarById(id)));
            perfEnd('  ↳ verrats cross-centre', 'api');
            const fetchedValid = fetched.filter((b): b is NonNullable<typeof b> => b !== null);
            if (fetchedValid.length > 0) {
                const hydrated = await boarsStore.hydrateBoars(fetchedValid);
                for (const boar of hydrated) {
                    boarsMap.set(boar.id, boar);
                    crossCenterBoars.set(boar.id, boar);
                }
            }
            // Ce que l'API n'a pas rendu : mémorisé pour ne pas être redemandé à chaque hydratation.
            for (const id of stillMissingIds) if (!boarsMap.has(id)) unresolvableBoarIds.add(id);
        }

        // Fallback 3 : agents cross-centre (lot externe reçu d'un autre centre)
        // Les agents diluteur/préleveur de l'autre centre ne sont pas dans le store local.
        const missingAgentIds = [...new Set([
            ...productions.map(p => p.agentDilutorId),
            ...productions.map(p => p.agentSamplerId),
        ].filter((id): id is number => !!id && !agentMap.has(id) && !unresolvableAgentIds.has(id)))];

        if (missingAgentIds.length > 0) {
            const agentResults = await Promise.all(missingAgentIds.map(id => AgentService.getById<IAgentApiResponse>(id)));
            for (const r of agentResults) {
                if (!r.error && r.data) {
                    const raw = r.data as IAgentApiResponse;
                    const agent = { ...raw } as unknown as IAgent;
                    agentMap.set(raw.id, agent);
                    crossCenterAgents.set(raw.id, agent);
                }
            }
            for (const id of missingAgentIds) if (!agentMap.has(id)) unresolvableAgentIds.add(id);
        }

        // 4. Hydrater les child entities en batch (optimisé O(1) via les stores child)
        const staticsToHydrate = productions.map(p => p.static).filter(s => s != null);
        const samplesToHydrate = productions.map(p => p.sample).filter(s => s != null);
        const dilutorBathsToHydrate = productions.flatMap(p => p.dilutorBaths ?? []);

        perfStart('  ↳ child entities (static, sample, bains)');
        const [hydratedStatics, hydratedSamples, hydratedDilutorBaths] = await Promise.all([
            staticsToHydrate.length > 0 ? productionStaticStore.hydrateStatics(staticsToHydrate as any) : [],
            samplesToHydrate.length > 0 ? productionSampleStore.hydrateSamples(samplesToHydrate as any) : [],
            dilutorBathsToHydrate.length > 0 ? productionDilutorBathStore.hydrateDilutorBaths(dilutorBathsToHydrate as any) : []
        ]);
        perfEnd('  ↳ child entities (static, sample, bains)', 'mapping');

        // Maps pour retrouver les child entities hydratés par leur ID
        const staticMap = new Map(hydratedStatics.map(s => [s.id, s]));
        const sampleMap = new Map(hydratedSamples.map(s => [s.id, s]));
        const dilutorBathMap = new Map(hydratedDilutorBaths.map(d => [d.id, d]));

        // 5. Hydrater toutes les productions en une seule passe
        const models: IProduction[] = [];

        for (const production of productions) {
            const model = { ...production } as unknown as IProduction;

            // Normaliser les enums : SignalR envoie des entiers, l'API HTTP envoie des strings.
            // Convertir les entiers (SignalR) en clé string pour que les comparaisons
            // .toString() == StateOfXxx[StateOfXxx.XXX] dans ejaculate.ts continuent de fonctionner.
            if (typeof (model.productionState as unknown) === 'number') {
                model.productionState = StateOfProduction[model.productionState] as unknown as StateOfProduction;
            }
            if (typeof (model.samplingState as unknown) === 'number') {
                model.samplingState = StateOfSampling[model.samplingState] as unknown as StateOfSampling;
            }

            // Lier les références (lookup O(1) via Map)
            // Fallback sur l'objet API si la référence n'est pas dans le store (ex: filtre centre)
            model.center = centerMap.get(production.centerId) ?? model.center;
            model.animal = boarsMap.get(production.animalId) ?? model.animal;
            model.agentDilutor = agentMap.get(production.agentDilutorId) ?? model.agentDilutor;
            model.agentSampler = agentMap.get(production.agentSamplerId) ?? model.agentSampler;
            model.technique = parameterMap.get(production.techniqueId) ?? model.technique;

            // Hydrater les child entities
            if (production.static?.id) {
                model.static = staticMap.get(production.static.id) ?? model.static;
            }
            if (production.sample?.id) {
                model.sample = sampleMap.get(production.sample.id) ?? model.sample;
            }
            if (production.dilutorBaths) {
                model.dilutorBaths = production.dilutorBaths
                    .map(db => dilutorBathMap.get(db.id) ?? db as any)
                    .filter(db => db != null);
            }
            // Note: samplingEvents ne nécessite pas d'hydratation supplémentaire pour le moment

            models.push(model);
        }

        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IProductionFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;

        // Les résolutions cross-centre sont relatives au centre chargé : un verrat « d'un autre
        // centre » pour le centre 11 peut être un verrat local du centre 1.
        if (loadedCenterId.value !== undefined && loadedCenterId.value !== filter.centerId) {
            clearCrossCenterCaches();
        }

        start('GET /Production (réseau)');
        let result = await ProductionService.getAll<IProductionFilter, IProductionApiResponse>(filter);
        end('GET /Production (réseau)', 'api');

        datas.value = [];

        if (!result.error) {
            start('hydrateProductions()');
            datas.value = await hydrateProductions(result.data as IProductionApiResponse[], filter.centerId, perfPage);
            end('hydrateProductions()', 'mapping');
        }

        isLoading.value = false;
        loadedCenterId.value = filter.centerId;
        loadedDateFrom.value = filter.dateFrom;
        return result;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données historique
     */
    async function loadHistorique(filter: IProductionFilter) {
        isLoading.value = true;
        let result = await ProductionService.getAll<IProductionFilter, IProductionApiResponse>(filter);
        isLoading.value = false;
        datasHistorique.value = [];

        if (!result.error) {
            datasHistorique.value = await hydrateProductions(result.data as IProductionApiResponse[], filter.centerId);
        }

        return result;
    }

    async function getWithSearchStore(id: number, searchStore: boolean = false) {
        if (searchStore) {
            let result = datas.value.find((a) => a.id == id);
            if (result) {
                current.value = result as IProduction;
                itemForm.value = toForm(result as IProduction);
                return result;
            }
        }

        let result = await ProductionService.getById<IProductionApiResponse>(id);

        if (!result.error) {
            const [modelData] = await hydrateProductions([result.data as IProductionApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }

        return result;
    }

    /**
     * Récupération d'une production par son identifiant
     * @param id
     */
    async function get(id: number) {
        let result = await ProductionService.getById<IProductionApiResponse>(id);

        if (!result.error) {
            const [modelData] = await hydrateProductions([result.data as IProductionApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0) {
                datas.value[index] = modelData;
            } else {
                // Ajouter au store si elle n'existe pas encore
                datas.value.push(modelData);
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }

        return result;
    }

    /**
     * Garantit qu'une production est présente et hydratée dans le store, sans toucher à `current`.
     * Contrairement à get(), qui répond à une sélection de l'utilisateur, cette méthode ne sert qu'à
     * combler un trou de cache pendant l'hydratation d'une autre entité : déplacer la production
     * courante à cette occasion ferait dériver les vues ouvertes.
     * Mirror de lotStore.ensureLoadedById / manufactoringProductionStore.ensureLoadedById.
     */
    async function ensureLoadedById(id: number): Promise<IProduction | undefined> {
        const existing = datas.value.find(p => p.id === id);
        if (existing) return existing;
        if (unresolvableProductionIds.has(id)) return undefined;

        const result = await ProductionService.getById<IProductionApiResponse>(id);
        if (result.error || !result.data) {
            // Un id que l'API ne rend pas ne sera pas rendu davantage à l'hydratation suivante.
            unresolvableProductionIds.add(id);
            return undefined;
        }

        const [model] = await hydrateProductions([result.data as IProductionApiResponse], loadedCenterId.value);
        if (datas.value.findIndex(p => p.id === model.id) === -1) {
            datas.value.push(model);
        }
        return model;
    }

    /**
     * Variante en masse de {@link ensureLoadedById} : une seule requête `Ids=` pour tous les ids
     * manquants, au lieu d'un GET par id. L'hydratation du suivi de fabrication en déclenchait
     * autant qu'il y avait de doses hors fenêtre ou d'un autre centre.
     *
     * Distinction volontaire entre les deux échecs possibles :
     *  - erreur réseau / API : on ne mémorise RIEN, l'absence n'est pas prouvée et une coupure
     *    passagère condamnerait les ids jusqu'au changement de centre ;
     *  - réponse valide où l'id est absent : mémorisé, il ne sera plus redemandé.
     */
    async function ensureLoadedByIds(ids: number[]): Promise<IProduction[]> {
        const wanted = [...new Set(ids)].filter(id => !!id && !unresolvableProductionIds.has(id));
        if (wanted.length === 0) return [];

        const resolved: IProduction[] = [];
        const missing: number[] = [];
        for (const id of wanted) {
            const existing = datas.value.find(p => p.id === id);
            if (existing) resolved.push(existing);
            else missing.push(id);
        }
        if (missing.length === 0) return resolved;

        const result = await ProductionService.getAll<IProductionFilter, IProductionApiResponse>({
            page: 1,
            pageSize: 0,
            ids: missing,
        });
        if (result.error || !result.data) return resolved;

        const models = await hydrateProductions(result.data as IProductionApiResponse[], loadedCenterId.value);
        for (const model of models) {
            if (datas.value.findIndex(p => p.id === model.id) === -1) {
                datas.value.push(model);
            }
            resolved.push(model);
        }

        const returned = new Set(models.map(m => m.id));
        for (const id of missing) {
            if (!returned.has(id)) unresolvableProductionIds.add(id);
        }
        return resolved;
    }

    /**
     * Récupération de l'objet dans le store
     * @param id Identifiant de l'objet
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IProduction);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        itemForm.value.sample.concentration = Math.floor(itemForm.value.sample.concentration);
        let result = {} as ServiceOperationResult<IProductionApiResponse | IDataOperationResult<IProductionApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await ProductionService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateProductions([result.data as IProductionApiResponse]);
                current.value = modelData;
                // Réhydrater les bains de dilution avec les ids attribués par le back. Sinon un bain
                // nouvellement ajouté (id=0) reste à 0 dans itemForm et serait RÉ-INSÉRÉ au save suivant
                // (ex. versement auto puis clic ENREGISTRER) → doublon de versement, volume dilué faussé.
                // Les dilutorBaths sont des enregistrements de versements (append-only), pas des champs
                // édités dans le formulaire : on peut les remplacer par la version persistée sans risque.
                const pDBStore = useProductionDilutorBathStore();
                itemForm.value.dilutorBaths = current.value.dilutorBaths?.map((a) => pDBStore.toForm(a)) ?? [];
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IProduction>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await ProductionService.create(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateProductions([result.data as IProductionApiResponse]);
                current.value = modelData;
                itemForm.value = toForm(current.value);
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    async function deleteProduction() {
        let result = await ProductionService.delete<IProductionApiResponse>(current.value.id);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem(undefined);
            itemForm.value = newForm(0);
        }

        return result;
    }

    async function destroy() {
        let result = await ProductionService.destroy(current.value.id);

        if (!result.error) {
            const [modelData] = await hydrateProductions([result.data as IProductionApiResponse]);
            current.value = modelData;
            let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
            if (index >= 0) {
                datas.value[index] = current.value;
            } else {
                const error = new DataOperationError();
                error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                return new ServiceOperationResult<IDataOperationResult<IProduction>>(true, { errors: error });
            }
        }

        return result;
    }

    function toForm(item: IProduction): IProductionForm {
        const eventsStore = useProductionSamplingEventStore();
        const dilutorStore = useProductionDilutorBathStore();
        const staticStore = useProductionStaticStore();
        const sampleStore = useProductionSampleStore();
        const form = {} as IProductionForm;
        form.id = item.id;
        form.dateProduction = item.dateProduction;
        form.observations = item.observations;
        form.nbrEtiquettes = item.nbrEtiquettes;
        form.numberProduction = item.numberProduction;
        form.numberDayProduction = item.numberDayProduction;
        form.productionState = item.productionState;
        form.samplingState = item.samplingState;

        form.animalId = item.animal?.id ?? item.animalId;
        form.centerId = item.center?.id ?? item.centerId;
        form.agentDilutorId = item.agentDilutor?.id ?? item.agentDilutorId;
        form.agentSamplerId = item.agentSampler?.id ?? item.agentSamplerId;
        form.techniqueId = item.technique?.id ?? item.techniqueId;

        form.samplingEvents = item.samplingEvents?.map((a) => eventsStore.toForm(a)) ?? [];

        if (item.sample)
            form.sample = sampleStore.toForm(item.sample);
        else
            form.sample = sampleStore.newForm(form.id);

        if (item.static)
            form.static = staticStore.toForm(item.static);
        else
            form.static = staticStore.newForm(form.id);

        form.dilutorBaths = item.dilutorBaths?.map((a) => dilutorStore.toForm(a)) ?? [];

        itemForm.value = form;
        return form;
    }

    function newForm(lienId: number): IProductionForm {
        const staticStore = useProductionStaticStore();
        const sampleStore = useProductionSampleStore();
        itemForm.value = {
            id: 0,
            centerId: lienId,
            dateProduction: new Date(),
            observations: "",
            animalId: 0,
            agentDilutorId: 0,
            agentSamplerId: 0,
            techniqueId: 0,
            nbrEtiquettes: 0,
            numberProduction: 1,
            numberDayProduction: 1,
            samplingEvents: [],
            dilutorBaths: [],
            sample: sampleStore.newForm(0),
            static: staticStore.newForm(0),
            productionState: 0,
            samplingState: 0,
            blankSample: false
        } as IProductionForm;
        return itemForm.value;
    }

    function newItem(lien?: ICenter): IProduction {
        const staticStore = useProductionStaticStore();
        const sampleStore = useProductionSampleStore();
        const agentStore = useAgentStore();
        const paramStore = useParameterStore();
        const animalStore = useBoars();

        current.value = {
            id: 0,
            center: lien,
            dateProduction: new Date(),
            observations: "",
            animal: animalStore.newItem(lien as ICenter),
            agentDilutor: agentStore.newItem(lien as ICenter),
            agentSampler: agentStore.newItem(lien as ICenter),
            technique: paramStore.newItem(lien as ICenter),
            nbrEtiquettes: 0,
            numberProduction: 1,
            numberDayProduction: 1,
            samplingEvents: [] as IProductionSamplingEvent[],
            dilutorBaths: [] as IProductionDilutorBath[],
            sample: sampleStore.newItem(),
            static: staticStore.newItem(),
        } as IProduction;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    // Retourner les propriétés et méthodes publiques
    return {
        // State
        datas,
        datasHistorique,
        current,
        itemForm,
        isLoading,
        loadedCenterId,
        loadedDateFrom,

        // Getters
        getProductionCreatedDuringTimespan,
        wereProductionCreatedDuringTimespan,
        getPreviousProductionDate,
        getNextProductionDate,
        getAnimalProductionNumber,
        getNumberProductionOfDay,
        getProductionNumberForDay,
        getStatusCodeProduction,
        getProductionsDesc,
        getProductionsAsc,

        // Actions
        load,
        loadHistorique,
        hydrateProductions,
        getWithSearchStore,
        get,
        ensureLoadedById,
        ensureLoadedByIds,
        searchStoreById,
        save,
        delete: deleteProduction,
        destroy,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajProduction", async (payload: IPayloadStore) => {
    const store = useProductionStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateProductions([payload.data as IProductionApiResponse]);
                store.datas.push(hydrated);

                // Les entités enfants (samplingEvents, dilutorBaths) sont créées en cascade
                // avec la production → pas de MajXxx individuel dispatché par le backend.
                // On les synchronise ici depuis les données déjà embarquées dans le payload hydraté.
                const pSEStore = useProductionSamplingEventStore();
                for (const se of hydrated.samplingEvents ?? []) {
                    if (pSEStore.datas.findIndex(x => x.id == se.id) == -1)
                        pSEStore.datas.push({ ...se, production: hydrated });
                }
                const pDBStore = useProductionDilutorBathStore();
                for (const db of hydrated.dilutorBaths ?? []) {
                    if (pDBStore.datas.findIndex(x => x.id == db.id) == -1)
                        pDBStore.datas.push({ ...db, production: hydrated });
                }
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateProductions([payload.data as IProductionApiResponse]);
                // Object.assign met à jour l'objet existant IN-PLACE au lieu de le remplacer.
                // Les lots (lotStore.datas[i].production) pointent vers cet objet — ils voient
                // immédiatement les nouvelles valeurs (samplingState, productionState, etc.)
                // sans avoir besoin d'un MajLot. Vue 3 détecte les mutations via son système Proxy.
                Object.assign(store.datas[idx], hydrated);

                // Sync des entités enfants (upsert)
                const pSEStore = useProductionSamplingEventStore();
                for (const se of hydrated.samplingEvents ?? []) {
                    const seIdx = pSEStore.datas.findIndex(x => x.id == se.id);
                    const seHydrated = { ...se, production: hydrated };
                    if (seIdx == -1) pSEStore.datas.push(seHydrated);
                    else pSEStore.datas[seIdx] = seHydrated;
                }
                const pDBStore = useProductionDilutorBathStore();
                for (const db of hydrated.dilutorBaths ?? []) {
                    const dbIdx = pDBStore.datas.findIndex(x => x.id == db.id);
                    const dbHydrated = { ...db, production: hydrated };
                    if (dbIdx == -1) pDBStore.datas.push(dbHydrated);
                    else pDBStore.datas[dbIdx] = dbHydrated;
                }
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);

            // Suppression des entités enfants dans leurs stores
            const pSEStore = useProductionSamplingEventStore();
            for (let i = pSEStore.datas.length - 1; i >= 0; i--) {
                if (pSEStore.datas[i].production?.id == payload.id)
                    pSEStore.datas.splice(i, 1);
            }
            const pDBStore = useProductionDilutorBathStore();
            for (let i = pDBStore.datas.length - 1; i >= 0; i--) {
                if (pDBStore.datas[i].production?.id == payload.id)
                    pDBStore.datas.splice(i, 1);
            }
            break;
        }
    }
});
